import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, Input, Switch } from "@/design-system";
import {
  queryKeys,
  useEnrollStudent,
  useLessonStudents,
  useStudents,
  useUnenrollStudent,
} from "@/api/queries";
import { displayName, matchesUserSearch } from "@/lib/user";
import { SHEET_OVERLAY_STYLE, SHEET_STYLE } from "@/components/sheetStyles";
import type { Lesson, Student } from "@/lib/types";

/**
 * Teacher-managed enrollment UI for `:assigned` lessons (issue #186).
 *
 * `StudentPicker` is the presentational two-list picker (enrolled +
 * available, with a search bar). `StudentPickerDialog` wraps it in a
 * bottom sheet and wires it to the enrollment API for an existing
 * lesson. The create form reuses the presentational picker directly as
 * a controlled, lesson-less selection list.
 */

/** Email-ordered comparator used by both picker lists. */
function byEmail(a: Student, b: Student): number {
  return a.email.localeCompare(b.email);
}

/** Insert a student into a cached roster list (idempotent), email-ordered. */
function addTo(list: Student[] | undefined, student: Student): Student[] {
  const next = list ?? [];
  if (next.some((s) => s.id === student.id)) return next;
  return [...next, student].sort(byEmail);
}

/** Drop a student from a cached roster list by id. */
function removeFrom(list: Student[] | undefined, studentId: number): Student[] {
  return (list ?? []).filter((s) => s.id !== studentId);
}

export interface StudentPickerProps {
  /** Full student roster. */
  students: Student[];
  /** Currently enrolled (dialog) or selected (create form) students. */
  selected: Student[];
  /** At/over capacity — the Add action is disabled. */
  atCapacity: boolean;
  /** A mutation is in flight; disables the Add/Remove controls. */
  busy?: boolean;
  /** Error from the last add/remove attempt. */
  error?: string | null;
  /** Roster still loading. */
  loading?: boolean;
  /**
   * Open lesson: a seat costs one credit, so candidates at 0 are blocked
   * (issue #248). `:assigned` lessons ignore the balance.
   */
  requiresCredits?: boolean;
  /** When true, seats are gifted and the credit balance is not required. */
  comp?: boolean;
  onAdd: (student: Student) => void;
  onRemove: (student: Student) => void;
  /** Prefix for test ids, e.g. `student-picker` / `lesson-form-picker`. */
  testId: string;
}

export function StudentPicker({
  students,
  selected,
  atCapacity,
  busy = false,
  error = null,
  loading = false,
  requiresCredits = false,
  comp = false,
  onAdd,
  onRemove,
  testId,
}: StudentPickerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  // A candidate who cannot cover an open seat, unless the teacher comps it.
  const cannotCover = (student: Student) =>
    requiresCredits && !comp && student.credit_balance <= 0;

  const selectedIds = useMemo(
    () => new Set(selected.map((s) => s.id)),
    [selected],
  );
  const available = useMemo(
    () => students.filter((s) => !selectedIds.has(s.id)).sort(byEmail),
    [students, selectedIds],
  );
  const shownSelected = useMemo(
    () => selected.filter((s) => matchesUserSearch(s, query)).sort(byEmail),
    [selected, query],
  );
  const shownAvailable = useMemo(
    () => available.filter((s) => matchesUserSearch(s, query)).sort(byEmail),
    [available, query],
  );

  if (loading) {
    return (
      <div
        data-testid={`${testId}-loading`}
        style={{ padding: "var(--space-6)", textAlign: "center", color: "var(--text-faint)" }}
      >
        {t("teacher.pickerLoading")}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
    >
      <Input
        label={t("teacher.pickerSearchLabel")}
        placeholder={t("teacher.pickerSearchPlaceholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid={`${testId}-search`}
      />

      {error && (
        <div
          role="alert"
          data-testid={`${testId}-error`}
          style={{
            background: "var(--status-danger-bg)",
            color: "var(--status-danger-fg)",
            padding: "var(--space-3) var(--space-4)",
            borderRadius: "var(--radius-md)",
            font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
          }}
        >
          {error}
        </div>
      )}

      <StudentList
        testId={`${testId}-enrolled`}
        heading={t("teacher.pickerEnrolled")}
        count={selected.length}
        students={shownSelected}
        emptyLabel={t("teacher.pickerEmptyEnrolled")}
        actionLabel={t("teacher.pickerRemove")}
        actionVariant="danger"
        disabled={busy}
        onAction={onRemove}
      />

      <StudentList
        testId={`${testId}-available`}
        heading={t("teacher.pickerAvailable")}
        count={available.length}
        students={shownAvailable}
        emptyLabel={t("teacher.pickerEmptyAvailable")}
        actionLabel={t("teacher.pickerAdd")}
        actionVariant="secondary"
        disabled={busy || atCapacity}
        disabledFor={cannotCover}
        showCredits={requiresCredits}
        onAction={onAdd}
      />

      {requiresCredits && !comp && (
        <div
          data-testid={`${testId}-credits-note`}
          style={{
            background: "var(--surface-muted)",
            color: "var(--text-muted)",
            padding: "var(--space-2) var(--space-4)",
            borderRadius: "var(--radius-md)",
            font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
          }}
        >
          {t("teacher.pickerCreditsNote")}
        </div>
      )}

      {atCapacity && (
        <div
          data-testid={`${testId}-at-capacity`}
          style={{
            background: "var(--status-warning-bg)",
            color: "var(--status-warning-fg)",
            padding: "var(--space-2) var(--space-4)",
            borderRadius: "var(--radius-md)",
            font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
          }}
        >
          {t("teacher.pickerAtCapacity")}
        </div>
      )}
    </div>
  );
}

function StudentList({
  testId,
  heading,
  count,
  students,
  emptyLabel,
  actionLabel,
  actionVariant,
  disabled,
  disabledFor,
  showCredits = false,
  onAction,
}: {
  testId: string;
  heading: string;
  count: number;
  students: Student[];
  emptyLabel: string;
  actionLabel: string;
  actionVariant: "secondary" | "danger";
  disabled: boolean;
  /** Per-row block (e.g. an open seat the student cannot afford). */
  disabledFor?: (student: Student) => boolean;
  /** Show the student's credit balance next to the action. */
  showCredits?: boolean;
  onAction: (student: Student) => void;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid={testId}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
            letterSpacing: "var(--ls-wide)",
            textTransform: "uppercase",
            color: "var(--text-faint)",
          }}
        >
          {heading}
        </span>
        <Badge tone="neutral">{count}</Badge>
      </div>

      {students.length === 0 ? (
        <div
          data-testid={`${testId}-empty`}
          style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
        >
          {emptyLabel}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {students.map((student) => (
            <div
              key={student.id}
              data-testid={`${testId}-row-${student.id}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "var(--space-2) var(--space-3)",
                background: "var(--surface-muted)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                    color: "var(--text-strong)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName(student)}
                </div>
                <div
                  style={{
                    font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                    color: "var(--text-muted)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {student.email}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
                {showCredits && (
                  <Badge
                    tone={student.credit_balance > 0 ? "neutral" : "warning"}
                    data-testid={`${testId}-credits-${student.id}`}
                  >
                    {t("teacher.creditsCount", { count: student.credit_balance })}
                  </Badge>
                )}
                <Button
                  variant={actionVariant}
                  size="sm"
                  disabled={disabled || (disabledFor?.(student) ?? false)}
                  onClick={() => onAction(student)}
                  data-testid={`${testId}-action-${student.id}`}
                  style={{ flex: "none" }}
                >
                  {actionLabel}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Bottom-sheet picker for an existing `:assigned` lesson. Loads the full
 * roster and the lesson's enrolled students, and add/removes them via the
 * enrollment API (optimistically moving a student between the two lists
 * and rolling back on error).
 */
export function StudentPickerDialog({
  token,
  lesson,
  onClose,
}: {
  token: string | null;
  lesson: Lesson;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Open lessons charge a credit unless the teacher comps the seat (#248).
  const [comp, setComp] = useState(false);

  const studentsQuery = useStudents(token, true);
  const enrolledQuery = useLessonStudents(token, lesson.id);
  const enrollMutation = useEnrollStudent(token);
  const unenrollMutation = useUnenrollStudent(token);

  const students = studentsQuery.data ?? [];
  const enrolled = enrolledQuery.data ?? [];
  const busy = enrollMutation.isPending || unenrollMutation.isPending;
  const atCapacity = enrolled.length >= lesson.capacity;
  const requiresCredits = lesson.lesson_type === "open";

  function add(student: Student) {
    setError(null);
    const key = queryKeys.lessonStudents(lesson.id);
    const previous = queryClient.getQueryData<Student[]>(key);
    queryClient.setQueryData<Student[]>(key, (old) => addTo(old, student));

    enrollMutation.mutate(
      { lessonId: lesson.id, studentId: student.id, ...(comp ? { comp: true } : {}) },
      {
        onError: (err) => {
          queryClient.setQueryData(key, previous);
          setError(err?.message ?? t("teacher.enrollError"));
        },
      },
    );
  }

  function remove(student: Student) {
    setError(null);
    const key = queryKeys.lessonStudents(lesson.id);
    const previous = queryClient.getQueryData<Student[]>(key);
    queryClient.setQueryData<Student[]>(key, (old) => removeFrom(old, student.id));

    unenrollMutation.mutate(
      { lessonId: lesson.id, studentId: student.id },
      {
        onError: (err) => {
          queryClient.setQueryData(key, previous);
          setError(err?.message ?? t("teacher.unenrollError"));
        },
      },
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("teacher.manageStudentsHeading")}
      data-testid="student-picker-dialog"
      style={SHEET_OVERLAY_STYLE}
      onClick={onClose}
    >
      <Card
        padding="var(--space-5)"
        onClick={(e) => e.stopPropagation()}
        style={SHEET_STYLE}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "var(--space-2)",
          }}
        >
          <div
            style={{
              font: "var(--fw-extra) var(--fs-xl)/1.1 var(--font-display)",
              color: "var(--text-strong)",
              letterSpacing: "var(--ls-tight)",
            }}
          >
            {t("teacher.manageStudentsHeading")}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="student-picker-dialog-close"
          >
            {t("common.close")}
          </Button>
        </div>

        <div
          style={{
            font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
            color: "var(--text-muted)",
            marginBottom: "var(--space-4)",
          }}
        >
          {lesson.title}
        </div>

        <StudentPicker
          testId="student-picker"
          students={students}
          selected={enrolled}
          atCapacity={atCapacity}
          busy={busy}
          error={error}
          loading={studentsQuery.isLoading || enrolledQuery.isLoading}
          requiresCredits={requiresCredits}
          comp={comp}
          onAdd={add}
          onRemove={remove}
        />

        {requiresCredits && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginTop: "var(--space-4)",
              padding: "var(--space-3) var(--space-4)",
              background: "var(--surface-muted)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <span
              style={{
                font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                color: "var(--text-strong)",
              }}
            >
              {t("teacher.pickerComp")}
            </span>
            <Switch
              checked={comp}
              onChange={setComp}
              disabled={busy}
              ariaLabel={t("teacher.pickerComp")}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
